import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  GET_CSRF_ENDPOINT,
  LICENSING_ENDPOINT,
  USERS_ENDPOINT,
} from "Constants";
import {
  GetBaseOrganizationUsageResponse,
  GetGroupUsageDetailsRequest,
  GetGroupUsageDetailsResponse,
  GetOrganizationUsageRequest,
  GetOrganizationUsageResponse,
  WhatsNewResponse,
  WhatsNewRequest as WhatsNewUpdateRequest,
} from "Types";
import {
  getRouteBasename,
  isShareableReportRoute,
  paramSerializer,
  resetStoreThunk,
  sessionExpiredAction,
} from "Utils";
import { getCsrfToken, getSharedToken } from "./baseApiUtils";

export type BaseQueryReturnType = ReturnType<typeof fetchBaseQuery>;

const getApiEndpoint = () => {
  const basename = getRouteBasename();

  if (typeof JEST_TESTS !== "undefined" && JEST_TESTS) {
    return `http://localhost${API_ENDPOINT}`;
  } else {
    return basename ? `${basename}${API_ENDPOINT}` : API_ENDPOINT; // API_ENDPOINT has leading slash
  }
};

const shareableReportBaseQuery = fetchBaseQuery({
  baseUrl: getApiEndpoint(),
  paramsSerializer: paramSerializer,
  prepareHeaders: (headers) => {
    const csrfToken = getCsrfToken();
    const sharedToken = getSharedToken();

    headers.set("X-CSRFToken", csrfToken);
    headers.set("Shared-Token", sharedToken);

    return headers;
  },
});

const baseQuery = fetchBaseQuery({
  baseUrl: getApiEndpoint(),
  paramsSerializer: paramSerializer,
  prepareHeaders: (headers) => {
    const csrfToken = getCsrfToken();

    headers.set("X-CSRFToken", csrfToken);

    return headers;
  },
});

const SELF_HANDLED_401_ENDPOINTS = [
  "downloadFilestreamFile",
  "downloadVersion",
];

const baseQueryWithAuth: BaseQueryReturnType = async (
  args,
  api,
  extraOptions,
) => {
  let result;

  if (isShareableReportRoute()) {
    result = await shareableReportBaseQuery(args, api, extraOptions);
  } else {
    result = await baseQuery(args, api, extraOptions);

    // Users can get logged out when session expires. In that case, we want to clear redux store
    // otherwise it is possible to have weird data in it. sessionExpiredAction keeps auth
    // initialized-but-signed-out so RequireAuth redirects to login instead of rendering blank.
    if (
      result &&
      result.error &&
      result.error.status === 401 &&
      !SELF_HANDLED_401_ENDPOINTS.includes(api.endpoint)
    ) {
      api.dispatch(resetStoreThunk());
      api.dispatch(sessionExpiredAction());
    }
  }

  return result;
};

export const baseApi = createApi({
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCSRFToken: build.query<void, void>({
      query: () => ({ url: `${GET_CSRF_ENDPOINT}` }),
    }),
    getWhatsNew: build.query<WhatsNewResponse, void>({
      query: () => ({ url: `${USERS_ENDPOINT}/whats-new` }),
    }),
    updateWhatsNew: build.mutation<void, WhatsNewUpdateRequest>({
      query: (data) => ({
        url: `${USERS_ENDPOINT}/whats-new`,
        method: "PATCH",
        body: data,
      }),
    }),
    getBaseOrganizationUsage: build.query<
      GetBaseOrganizationUsageResponse,
      GetOrganizationUsageRequest
    >({
      query: (queryArg) => ({
        url: `${LICENSING_ENDPOINT}/usage/organizations/${queryArg.organizationId}`,
      }),
      providesTags: ["Usage"],
    }),
    getOrganizationUsage: build.query<
      GetOrganizationUsageResponse,
      GetOrganizationUsageRequest
    >({
      query: (queryArg) => ({
        url: `${LICENSING_ENDPOINT}/usage/organizations/${queryArg.organizationId}`,
        params: {
          get_extended: true,
        },
      }),
      providesTags: ["SiteKeys", "Usage"],
    }),
    getGroupUsageDetails: build.query<
      GetGroupUsageDetailsResponse,
      GetGroupUsageDetailsRequest
    >({
      query: (queryArg) => ({
        url: `${LICENSING_ENDPOINT}/usage/groups/${queryArg.groupId}`,
      }),
      providesTags: ["Usage"],
    }),
  }),
  tagTypes: [
    "Authentication",
    "Files",
    "GroupPolicy",
    "Groups",
    "InsightsSearches",
    "LatestReport",
    "Organizations",
    "OrganizationPolicy",
    "Package",
    "SharedReports",
    "SiteKeys",
    "SoftwareCatalog",
    "SSO",
    "Usage",
    "Users",
    "Whoami",
  ],
  // We want to refetch when component mounts due to auth
  // If user is logged out, and they log back in, cached 401 errors will be shown
  keepUnusedDataFor: 0,
});

export const {
  useGetCSRFTokenQuery,
  useGetWhatsNewQuery,
  useUpdateWhatsNewMutation,
  useGetBaseOrganizationUsageQuery,
  useGetOrganizationUsageQuery,
  useGetGroupUsageDetailsQuery,
} = baseApi;
